import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Socle commun des éditeurs vectoriels (plan d'appartement ET plan de masse) — V15.39.
 * Zoom 1.0 = ajustement du fond au conteneur ; pan {0,0} = centré.
 * Molette = zoom autour du pointeur · Ctrl/⌘ + glisser, bouton du milieu ou Espace maintenu = déplacement.
 */
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 8;
export const ZOOM_STEP = 1.2;
export const DEFAULT_VIEWPORT = { zoom: 1, panX: 0, panY: 0 };

const clampZoom = (z) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z || 1));

/** Transformation « fit » normalisé [0..1] → pixels du Stage, zoom et pan inclus. */
export const fitTransform = (size, imgDims, viewport) => {
  const W = size.width, H = size.height;
  const iw = imgDims?.width, ih = imgDims?.height;
  const fit = iw && ih ? Math.min(W / iw, H / ih) : null;
  const baseW = fit ? iw * fit : W, baseH = fit ? ih * fit : H;
  const zoom = clampZoom(viewport?.zoom);
  const imgW = baseW * zoom, imgH = baseH * zoom;
  return { offsetX: (W - imgW) / 2 + (viewport?.panX || 0), offsetY: (H - imgH) / 2 + (viewport?.panY || 0), scaleX: imgW, scaleY: imgH, imgW, imgH, zoom };
};

export const useFitTransform = (size, imgDims, viewport) => {
  const transform = useMemo(() => fitTransform(size, imgDims, viewport), [size, imgDims, viewport]);
  const norm2px = useCallback((nx, ny) => ({ x: transform.offsetX + nx * transform.scaleX, y: transform.offsetY + ny * transform.scaleY }), [transform]);
  const px2norm = useCallback((px, py) => ({ x: (px - transform.offsetX) / Math.max(1, transform.scaleX), y: (py - transform.offsetY) / Math.max(1, transform.scaleY) }), [transform]);
  return { transform, norm2px, px2norm };
};

/**
 * useCanvasViewport — gestes de navigation d'un Stage Konva.
 * @param stageRef ref du Stage · size {width,height} · viewport/setViewport état contrôlé (useState ou contexte).
 * Retourne les handlers à brancher sur le Stage + helpers (zoomBy, resetView, isPanning, spaceHeld).
 */
export const useCanvasViewport = ({ stageRef, size, viewport, setViewport }) => {
  const [panAnchor, setPanAnchor] = useState(null);
  const [spaceHeld, setSpaceHeld] = useState(false);

  useEffect(() => {
    const isTyping = (ev) => /^(INPUT|TEXTAREA|SELECT)$/.test(ev.target?.tagName || '') || ev.target?.isContentEditable;
    const down = (ev) => { if (ev.code === 'Space' && !isTyping(ev)) { ev.preventDefault(); setSpaceHeld(true); } };
    const up = (ev) => { if (ev.code === 'Space') setSpaceHeld(false); };
    window.addEventListener('keydown', down); window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  const zoomAt = useCallback((factor, pointer) => {
    const oldZoom = clampZoom(viewport?.zoom);
    const newZoom = clampZoom(oldZoom * factor);
    if (Math.abs(newZoom - oldZoom) < 1e-3) return;
    const W = size.width, H = size.height;
    const p = pointer || { x: W / 2, y: H / 2 };
    const dx = p.x - (W / 2 + (viewport?.panX || 0)), dy = p.y - (H / 2 + (viewport?.panY || 0));
    const k = newZoom / oldZoom;
    setViewport({ zoom: newZoom, panX: (viewport?.panX || 0) + dx * (1 - k), panY: (viewport?.panY || 0) + dy * (1 - k) });
  }, [viewport, size, setViewport]);

  const handleWheel = useCallback((e) => {
    e.evt?.preventDefault();
    const pointer = stageRef.current?.getPointerPosition();
    if (!pointer) return;
    zoomAt((e.evt?.deltaY || 0) > 0 ? 1 / 1.12 : 1.12, pointer);
  }, [stageRef, zoomAt]);

  const handleMouseDown = useCallback((e) => {
    if (!(e.evt?.ctrlKey || e.evt?.metaKey || e.evt?.button === 1 || spaceHeld)) return false;
    const p = stageRef.current?.getPointerPosition();
    if (!p) return false;
    e.evt?.preventDefault();
    setPanAnchor({ px: p.x, py: p.y, startPanX: viewport?.panX || 0, startPanY: viewport?.panY || 0 });
    return true;
  }, [stageRef, viewport, spaceHeld]);
  const handleMouseUp = useCallback(() => setPanAnchor(null), []);

  useEffect(() => {
    if (!panAnchor) return undefined;
    const onMove = (ev) => {
      const rect = stageRef.current?.container().getBoundingClientRect();
      if (!rect) return;
      setViewport((v) => ({ ...v, panX: panAnchor.startPanX + (ev.clientX - rect.left - panAnchor.px), panY: panAnchor.startPanY + (ev.clientY - rect.top - panAnchor.py) }));
    };
    const onUp = () => setPanAnchor(null);
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [panAnchor, setViewport, stageRef]);

  const zoomBy = useCallback((factor) => zoomAt(factor, null), [zoomAt]);
  const resetView = useCallback(() => setViewport({ ...DEFAULT_VIEWPORT }), [setViewport]);
  return { handleWheel, handleMouseDown, handleMouseUp, zoomBy, resetView, isPanning: !!panAnchor, spaceHeld };
};

/** Raccourcis clavier d'éditeur : { key: handler } (key = e.key ou 'Delete' ; ignorés dans les champs de saisie). */
export const useEditorShortcuts = (bindings, enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || '') || e.target?.isContentEditable || e.ctrlKey || e.metaKey || e.altKey) return;
      const h = bindings[e.key] || bindings[e.key?.toLowerCase?.()] || (e.key === 'Backspace' ? bindings.Delete : null);
      if (h) { e.preventDefault(); h(e); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bindings, enabled]);
};
